import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { AppInput, colorTokens } from '@eruvaka/web-pondlogs-base';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { initiateEmailChange, verifyEmailChange, initiatePhoneChange, verifyPhoneChange, updateUserProfile, } from '../../api/userApi';
import { initiateSubUserEmailChange, verifySubUserEmailChange, initiateSubUserPhoneChange, verifySubUserPhoneChange, updateSubUser, } from '../../api/subUsersApi';
const OTP_RESEND_SECONDS = 119;
const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
};
const OTPVerificationDialog = memo(function OTPVerificationDialog({ open, onClose, type, userId, currentValue, isIndia = false, onSuccess, }) {
    const { t } = useTranslation('main');
    // Non-India phone: skip OTP, use direct update
    const useDirectUpdate = type === 'phone' && !isIndia;
    // ── state ─────────────────────────────────────────────────────────────────
    const [step, setStep] = useState('input');
    const [newValue, setNewValue] = useState('');
    const [otp, setOtp] = useState('');
    const [inputError, setInputError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const timerRef = useRef(null);
    // ── reset on open/close ───────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            setStep('input');
            setNewValue('');
            setOtp('');
            setInputError('');
            setOtpError('');
            setSending(false);
            setVerifying(false);
            setCountdown(0);
        }
        else {
            if (timerRef.current)
                clearInterval(timerRef.current);
        }
    }, [open]);
    // ── countdown timer ───────────────────────────────────────────────────────
    const startCountdown = useCallback(() => {
        if (timerRef.current)
            clearInterval(timerRef.current);
        setCountdown(OTP_RESEND_SECONDS);
        timerRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);
    // ── validate step 1 input ─────────────────────────────────────────────────
    const validateInput = () => {
        if (!newValue.trim()) {
            setInputError(type === 'email'
                ? t('profileUsers.otpErrorEmailRequired')
                : t('profileUsers.otpErrorPhoneRequired'));
            return false;
        }
        if (type === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(newValue.trim())) {
                setInputError(t('profileUsers.otpErrorEmailInvalid'));
                return false;
            }
        }
        return true;
    };
    // ── direct update (non-India phone) ──────────────────────────────────────
    const handleDirectUpdate = async () => {
        if (!validateInput())
            return;
        setSending(true);
        setInputError('');
        try {
            if (userId) {
                await updateSubUser(userId, { phone: newValue.trim() });
            }
            else {
                await updateUserProfile({ phoneNumber: newValue.trim() });
            }
            onSuccess(newValue.trim());
            onClose();
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setInputError(msg || t('profileUsers.otpErrorSendFailed'));
        }
        finally {
            setSending(false);
        }
    };
    // ── send OTP ──────────────────────────────────────────────────────────────
    const handleSendOTP = async () => {
        if (!validateInput())
            return;
        setSending(true);
        setInputError('');
        try {
            if (userId) {
                if (type === 'email') {
                    await initiateSubUserEmailChange(userId, newValue.trim());
                }
                else {
                    await initiateSubUserPhoneChange(userId, newValue.trim());
                }
            }
            else {
                if (type === 'email') {
                    await initiateEmailChange(newValue.trim());
                }
                else {
                    await initiatePhoneChange(newValue.trim());
                }
            }
            setStep('otp');
            setOtp('');
            setOtpError('');
            startCountdown();
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setInputError(msg || t('profileUsers.otpErrorSendFailed'));
        }
        finally {
            setSending(false);
        }
    };
    // ── resend OTP ────────────────────────────────────────────────────────────
    const handleResend = async () => {
        setSending(true);
        setOtpError('');
        setOtp('');
        try {
            if (userId) {
                if (type === 'email') {
                    await initiateSubUserEmailChange(userId, newValue.trim());
                }
                else {
                    await initiateSubUserPhoneChange(userId, newValue.trim());
                }
            }
            else {
                if (type === 'email') {
                    await initiateEmailChange(newValue.trim());
                }
                else {
                    await initiatePhoneChange(newValue.trim());
                }
            }
            startCountdown();
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setOtpError(msg || t('profileUsers.otpErrorSendFailed'));
        }
        finally {
            setSending(false);
        }
    };
    // ── verify OTP ────────────────────────────────────────────────────────────
    const handleVerify = async () => {
        if (otp.length !== 6) {
            setOtpError(t('profileUsers.otpErrorInvalidOTP'));
            return;
        }
        setVerifying(true);
        setOtpError('');
        try {
            if (userId) {
                if (type === 'email') {
                    await verifySubUserEmailChange(userId, otp);
                }
                else {
                    await verifySubUserPhoneChange(userId, otp);
                }
            }
            else {
                if (type === 'email') {
                    await verifyEmailChange(otp);
                }
                else {
                    await verifyPhoneChange(otp);
                }
            }
            onSuccess(newValue.trim());
            onClose();
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setOtpError(msg || t('profileUsers.otpErrorVerifyFailed'));
        }
        finally {
            setVerifying(false);
        }
    };
    const title = type === 'email' ? t('profileUsers.otpTitleEmail') : t('profileUsers.otpTitlePhone');
    const oldLabel = type === 'email' ? t('profileUsers.otpOldEmail') : t('profileUsers.otpOldPhone');
    const inputLabel = type === 'email' ? t('profileUsers.otpLabelNewEmail') : t('profileUsers.otpLabelNewPhone');
    const descText = type === 'email'
        ? t('profileUsers.otpDescEmail')
        : isIndia
            ? t('profileUsers.otpDescPhone')
            : undefined;
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "xs", fullWidth: true, PaperProps: { sx: { borderRadius: 2 } }, children: [_jsxs(DialogTitle, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: colorTokens.darkGrey,
                }, children: [title, _jsx(IconButton, { size: "small", onClick: onClose, "aria-label": "close", children: _jsx(CloseIcon, { fontSize: "small", sx: { color: colorTokens.magenta } }) })] }), _jsx(DialogContent, { sx: { pt: 1, pb: 1 }, children: step === 'input' ? (_jsxs(Box, { children: [currentValue && (_jsxs(Typography, { variant: "body2", sx: { mb: 1.5, fontSize: 13 }, children: [_jsxs("strong", { children: [oldLabel, ": "] }), currentValue] })), _jsx(Typography, { variant: "body2", sx: { fontWeight: 600, mb: 0.75, fontSize: 14 }, children: inputLabel }), _jsx(AppInput, { type: type === 'email' ? 'email' : 'tel', value: newValue, onChange: (e) => {
                                setNewValue(e.target.value);
                                if (inputError)
                                    setInputError('');
                            }, error: !!inputError, helperText: inputError || undefined, 
                            // autoFocus
                            // inputProps={
                            //   type === 'phone'
                            //     ? { placeholder: '1234567890' }
                            //     : { placeholder: t('profileUsers.otpEmailPlaceholder') }
                            // }
                            onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    void (useDirectUpdate ? handleDirectUpdate() : handleSendOTP());
                            } }), descText && (_jsx(Typography, { variant: "caption", sx: { display: 'block', mt: 1.5, color: 'text.secondary', fontSize: 12 }, children: descText }))] })) : (_jsxs(Box, { children: [_jsx(Typography, { variant: "body2", sx: { mb: 1.5, fontSize: 13, color: 'text.secondary' }, children: type === 'email'
                                ? t('profileUsers.otpSentEmail', { email: newValue })
                                : t('profileUsers.otpSentPhone', { phone: newValue }) }), _jsx(AppInput, { value: otp, onChange: (e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setOtp(val);
                                if (otpError)
                                    setOtpError('');
                            }, error: !!otpError, helperText: otpError || undefined, 
                            // autoFocus
                            // inputProps={{
                            //   maxLength: 6,
                            //   inputMode: 'numeric',
                            //   pattern: '[0-9]*',
                            //   placeholder: t('profileUsers.otpLabelCode'),
                            // }}
                            onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    void handleVerify();
                            } }), _jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mt: 1.5,
                            }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", children: countdown > 0
                                        ? t('profileUsers.otpResendIn', { timer: formatTimer(countdown) })
                                        : '' }), _jsx(Button, { size: "small", variant: "text", onClick: () => void handleResend(), disabled: sending || countdown > 0, sx: {
                                        minWidth: 0,
                                        p: 0,
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        color: colorTokens.primary,
                                    }, children: sending ? _jsx(CircularProgress, { size: 12 }) : t('profileUsers.otpResendBtn') })] })] })) }), _jsx(DialogActions, { sx: { px: 3, pb: 2, pt: 1.5, justifyContent: 'center' }, children: step === 'otp' ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "outlined", onClick: () => setStep('input'), size: "small", sx: {
                                minWidth: 90,
                                color: colorTokens.darkGrey,
                                borderColor: colorTokens.borderLight,
                            }, children: t('profileUsers.otpBack') }), _jsx(Button, { variant: "contained", onClick: () => void handleVerify(), disabled: verifying || otp.length !== 6, size: "small", sx: {
                                minWidth: 90,
                                bgcolor: colorTokens.primary,
                                '&:hover': { bgcolor: colorTokens.primaryDark },
                            }, startIcon: verifying ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : undefined, children: t('profileUsers.otpVerifyBtn') })] })) : useDirectUpdate ? (_jsx(Button, { variant: "contained", onClick: () => void handleDirectUpdate(), disabled: sending || !newValue.trim(), size: "small", sx: {
                        minWidth: 110,
                        bgcolor: colorTokens.primary,
                        '&:hover': { bgcolor: colorTokens.primaryDark },
                    }, children: t('profileUsers.otpUpdate') })) : (_jsx(Button, { variant: "contained", onClick: () => void handleSendOTP(), disabled: sending || !newValue.trim(), size: "small", sx: {
                        minWidth: 110,
                        bgcolor: colorTokens.primary,
                        '&:hover': { bgcolor: colorTokens.primaryDark },
                    }, startIcon: sending ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : undefined, children: t('profileUsers.otpSendBtn') })) })] }));
});
export default OTPVerificationDialog;
